import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LogOut, Phone, Calendar, MessageSquare, Trash2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    const token = localStorage.getItem('adminToken');
    
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      const response = await axios.get(`${API}/inquiries`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setInquiries(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Platnost přihlášení vypršela');
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Chyba při načítání poptávek');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Odhlášení úspěšné');
    navigate('/admin');
  };

  const handleDelete = async (inquiryId, customerName) => {
    if (!window.confirm(`Opravdu chcete smazat poptávku od ${customerName}?`)) {
      return;
    }

    const token = localStorage.getItem('adminToken');
    
    try {
      await axios.delete(`${API}/inquiries/${inquiryId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success('Poptávka byla smazána');
      // Reload inquiries
      loadInquiries();
    } catch (error) {
      toast.error('Chyba při mazání poptávky');
      console.error('Error deleting inquiry:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Načítám poptávky...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-blue-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600">Přehled poptávek</p>
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-blue-900 text-blue-900 hover:bg-blue-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Odhlásit se
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Celkem poptávek</p>
                  <p className="text-3xl font-bold text-blue-900">{inquiries.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-blue-900" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Inquiries List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Všechny poptávky</h2>
          
          {inquiries.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Zatím nemáte žádné poptávky</p>
              </CardContent>
            </Card>
          ) : (
            inquiries.map((inquiry) => (
              <Card key={inquiry.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg text-blue-900">{inquiry.name}</CardTitle>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(inquiry.created_at)}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(inquiry.id, inquiry.name)}
                      className="border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-gray-600" />
                      <a 
                        href={`tel:${inquiry.phone}`}
                        className="text-blue-900 hover:underline font-medium"
                      >
                        {inquiry.phone}
                      </a>
                    </div>
                    {inquiry.city && inquiry.street && (
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-gray-600" />
                        <span className="text-gray-700">
                          {inquiry.street}, {inquiry.city}
                        </span>
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <MessageSquare className="w-4 h-4 text-gray-600 mt-1" />
                      <p className="text-gray-700 flex-1">{inquiry.message}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
